import React, { useState, useEffect, useCallback } from 'react';
import { fetchInventoryItems, performInventoryOutbound, performInventoryInbound, updateInventoryByRFID, updatePalletNumber, getBatchPallet, type InventoryItem } from '../api/inventory';
import FormButton from '../components/FormButton';
import { useFirestoreSync } from '../hooks/useFirestoreSync';

const Inventory = () => {
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'query' | 'pending' | 'inbound' | 'outbound'>('query');
  const [searchMode, setSearchMode] = useState<'pallet' | 'rfid' | 'batch'>('pallet');
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  // RFID格式處理
  const RFID_PREFIX = '2400BA202403010';
  
  // 從完整RFID取得後五碼
  const getRfidLast5 = (fullRfid: string): string => {
    if (!fullRfid || fullRfid.startsWith('OUTBOUND_')) return '';
    return fullRfid.length >= 5 ? fullRfid.slice(-5) : fullRfid;
  };
  
  // 從後五碼組成完整RFID
  const getFullRfid = (last5: string): string => {
    if (!last5 || last5.length !== 5) return '';
    return RFID_PREFIX + last5;
  };
  
  // 安全的日期格式化函數
  const formatDate = (dateString: string): string => {
    if (!dateString) return '無日期';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return '無效日期';
      }
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      console.error('Date formatting error:', error, 'Input:', dateString);
      return '日期錯誤';
    }
  };
  
  // 出入庫作業相關state
  const [operationMode, setOperationMode] = useState<'single-inbound' | 'batch-inbound' | 'batch-outbound' | 'batch-query'>('single-inbound');
  const [inboundBatchNumber, setInboundBatchNumber] = useState('');
  const [inboundRFID, setInboundRFID] = useState('');
  
  // 批號查詢相關state
  const [queryBatchNumber, setQueryBatchNumber] = useState('');
  const [batchQueryResult, setBatchQueryResult] = useState<any>(null);
  const [batchQueryLoading, setBatchQueryLoading] = useState(false);
  
  // 編輯棧板號碼相關state
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [newPalletNumber, setNewPalletNumber] = useState('');

  // 載入庫存資料的函數
  const loadInventory = useCallback(async () => {
    try {
      setLoading(true);
      // 使用新的 API，根據 activeTab 決定 status
      let status = 'all';
      if (activeTab === 'query') {
        status = 'normal';  // 顯示有棧板號碼且未出庫的項目
      } else if (activeTab === 'pending') {
        status = 'pending';  // 無棧板號碼
      } else if (activeTab === 'outbound') {
        status = 'out';  // 已出庫
      }
      
      const response = await fetchInventoryItems(status);
      // 新 API 回應格式
      const responseItems = response.data.items;
      setItems(responseItems);
      setFilteredItems(responseItems);
    } catch (err) {
      console.error('Error in loadInventory:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  // 使用 Firestore 同步
  useFirestoreSync('inventory', loadInventory, true);

  useEffect(() => {
    loadInventory();
  }, [activeTab, loadInventory]);

  // 搜尋功能
  const performSearch = useCallback((value: string) => {
    if (!value.trim()) {
      setFilteredItems(items);
      setCurrentPage(1);
      return;
    }

    const filtered = items.filter(item => {
      const searchLower = value.toLowerCase();
      
      switch (searchMode) {
        case 'pallet':
          return item.palletNumber?.toLowerCase().includes(searchLower);
        case 'rfid':
          // 處理 RFID 搜尋：如果是已出庫項目（OUTBOUND_開頭），則不搜尋
          if (!item.rfid || item.rfid.startsWith('OUTBOUND_')) {
            return false;
          }
          // 搜尋RFID末五碼，確保 RFID 長度足夠
          const rfidLast5 = item.rfid.length >= 5 ? item.rfid.slice(-5) : item.rfid;
          return rfidLast5.toLowerCase().includes(searchLower);
        case 'batch':
          return item.name?.toLowerCase().includes(searchLower);
        default:
          return true;
      }
    });
    
    // 如果是查詢頁面，進一步過濾掉已出庫項目
    if (activeTab === 'query') {
      const activeFiltered = filtered.filter(item => 
        item.palletNumber !== 'OUT' && item.palletNumber !== '' && item.palletNumber !== null
      );
      setFilteredItems(activeFiltered);
    } else {
      setFilteredItems(filtered);
    }
    setCurrentPage(1); // 重置到第一頁
  }, [items, searchMode, activeTab]);

  const handleSearch = () => {
    performSearch(searchValue);
  };

  // 當搜尋模式改變時，如果有搜尋值，重新執行搜尋
  useEffect(() => {
    if (searchValue.trim()) {
      performSearch(searchValue);
    }
  }, [searchMode, performSearch, searchValue]);

  // 出庫功能
  const handleOutbound = async (item: InventoryItem) => {
    // 檢查RFID是否存在且未出庫
    if (!item.rfid || item.rfid.trim() === '' || item.rfid.startsWith('OUTBOUND_')) {
      alert('此項目沒有RFID號碼或已出庫，無法執行出庫操作');
      return;
    }
    
    const confirmed = window.confirm(
      `確定要將以下項目出庫嗎？\n\n` +
      `批號: ${item.name}\n` +
      `RFID後五碼: ${getRfidLast5(item.rfid)}\n` +
      `棧板號: ${item.palletNumber}\n\n` +
      `出庫後將無法在庫存列表中看到此項目！`
    );
    
    if (confirmed) {
      try {
        // 使用 performInventoryOutbound 函數
        await performInventoryOutbound(item.rfid);
        alert('出庫成功！');
        
        // 重新載入數據
        // 重新載入資料
        let status = 'all';
        if (activeTab === 'query') {
          status = 'all';
        } else if (activeTab === 'pending') {
          status = 'pending';
        } else if (activeTab === 'outbound') {
          status = 'out';
        }
        
        const response = await fetchInventoryItems(status);
        const responseItems = response.data.items;
        setItems(responseItems);
        setFilteredItems(responseItems);
      } catch (error) {
        alert('出庫失敗：' + (error instanceof Error ? error.message : '未知錯誤'));
      }
    }
  };

  // 清除搜尋
  const clearSearch = () => {
    setSearchValue('');
    setFilteredItems(items);
    setCurrentPage(1);
  };

  // 入庫處理函數
  const handleInbound = async () => {
    if (!inboundBatchNumber.trim() || !inboundRFID.trim()) {
      alert('請輸入批號和RFID後五碼');
      return;
    }

    // 檢查RFID後五碼格式
    if (inboundRFID.length !== 5) {
      alert('RFID後五碼必須是5位數字');
      return;
    }

    // 組成完整RFID
    const fullRfid = getFullRfid(inboundRFID);

    const confirmed = window.confirm(
      `確定要入庫以下項目嗎？\n\n` +
      `批號: ${inboundBatchNumber}\n` +
      `RFID後五碼: ${inboundRFID}\n` +
      `完整RFID: ${fullRfid}`
    );

    if (confirmed) {
      try {
        // 呼叫入庫API，傳送完整RFID
        await performInventoryInbound(fullRfid, inboundBatchNumber);
        alert('入庫成功！');
        
        // 清空表單
        setInboundBatchNumber('');
        setInboundRFID('');
        
        // 重新載入數據
        // 重新載入資料
        let status = 'all';
        if (activeTab === 'query') {
          status = 'all';
        } else if (activeTab === 'pending') {
          status = 'pending';
        } else if (activeTab === 'outbound') {
          status = 'out';
        }
        
        const response = await fetchInventoryItems(status);
        const responseItems = response.data.items;
        setItems(responseItems);
        setFilteredItems(responseItems);
      } catch (error) {
        alert('入庫失敗：' + (error instanceof Error ? error.message : '未知錯誤'));
      }
    }
  };

  // 處理更新棧板號碼
  const handleUpdatePalletNumber = async (item: InventoryItem) => {
    if (!newPalletNumber.trim()) {
      alert('請輸入棧板號碼');
      return;
    }

    try {
      await updatePalletNumber(item.rfid, newPalletNumber);
      alert('棧板號碼更新成功！');
      
      // 重新載入數據
      // 重新載入當前頁面的資料
      let status = 'all';
      if (activeTab === 'query') {
        status = 'normal';
      } else if (activeTab === 'pending') {
        status = 'pending';
      } else if (activeTab === 'outbound') {
        status = 'out';
      }
      
      const response = await fetchInventoryItems(status);
      const responseItems = response.data.items;
      setItems(responseItems);
      setFilteredItems(responseItems);
      
      // 清空編輯狀態
      setEditingItemId(null);
      setNewPalletNumber('');
    } catch (error) {
      alert('更新失敗：' + (error instanceof Error ? error.message : '未知錯誤'));
    }
  };

  // 開始編輯棧板號碼
  const startEditPallet = (item: InventoryItem) => {
    setEditingItemId(item.id);
    setNewPalletNumber(item.palletNumber || '');
  };

  // 取消編輯
  const cancelEditPallet = () => {
    setEditingItemId(null);
    setNewPalletNumber('');
  };
  
  // 批號查詢功能
  const handleBatchQuery = async () => {
    if (!queryBatchNumber.trim()) {
      alert('請輸入批號');
      return;
    }
    
    setBatchQueryLoading(true);
    setBatchQueryResult(null);
    
    try {
      const result = await getBatchPallet(queryBatchNumber);
      setBatchQueryResult(result);
    } catch (error) {
      alert('查詢失敗：' + (error instanceof Error ? error.message : '未知錯誤'));
      setBatchQueryResult(null);
    } finally {
      setBatchQueryLoading(false);
    }
  };


  // 計算分頁數據
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredItems.slice(startIndex, endIndex);
  

  // 換頁函數
  const handlePageChange = (page: number) => {
    // 確保頁碼在有效範圍內
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  if (loading) {
    return <div className="p-4">載入庫存資料中...</div>;
  }

  if (error) {
    return <div className="p-4 text-red-500">Error: {error}</div>;
  }

  // 確保 items 是陣列
  if (!Array.isArray(items)) {
    return <div className="p-4">無庫存資料。</div>;
  }

  const tabs = [
    { id: 'query', label: '查詢', icon: '🔍' },
    { id: 'pending', label: '待清點', icon: '📋' },
    { id: 'inbound', label: '出入庫作業', icon: '📦' },
    { id: 'outbound', label: '已出庫', icon: '📤' }
  ] as const;

  const searchModes = [
    { value: 'pallet', label: '棧板板號' },
    { value: 'rfid', label: 'RFID末五碼' },
    { value: 'batch', label: '批號' }
  ] as const;

  return (
    <div className="container mx-auto p-6">
      <div className="bg-white rounded-lg shadow-sm">
        {/* 頁面標題 */}
        <div className="border-b border-gray-200 px-6 py-4">
          <h1 className="text-2xl font-bold text-gray-900">庫存管理系統</h1>
          <p className="text-sm text-gray-600 mt-1">管理和監控庫存項目的詳細資訊</p>
        </div>

        {/* 分頁導航 */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* 分頁內容 */}
        <div className="p-6">
          {activeTab === 'query' && (
            <div>
              {/* 搜尋區域 */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex flex-wrap items-end gap-4">
                  <div className="flex-2 min-w-[300px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      搜尋值
                    </label>
                    <input
                      type="text"
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      placeholder={`請輸入${searchModes.find(m => m.value === searchMode)?.label}...`}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                  </div>
                  
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      搜尋模式
                    </label>
                    <select
                      value={searchMode}
                      onChange={(e) => setSearchMode(e.target.value as typeof searchMode)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {searchModes.map((mode) => (
                        <option key={mode.value} value={mode.value}>
                          {mode.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="flex gap-2">
                    <FormButton caption="搜尋" onClick={handleSearch} />
                    <FormButton caption="清除" onClick={clearSearch} />
                  </div>
                </div>
              </div>

              {/* 庫存表格 */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                        批號
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                        RFID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                        棧板號碼
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                        操作
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                        最後更新日期
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentItems.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                          {getRfidLast5(item.rfid)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {editingItemId === item.id ? (
                            <div className="flex items-center space-x-1">
                              <input
                                type="text"
                                value={newPalletNumber}
                                onChange={(e) => setNewPalletNumber(e.target.value)}
                                placeholder="輸入棧板號碼"
                                className="w-20 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                autoFocus
                              />
                              <button
                                onClick={() => handleUpdatePalletNumber(item)}
                                className="inline-flex items-center px-1.5 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                                title="確認"
                              >
                                ✓
                              </button>
                              <button
                                onClick={cancelEditPallet}
                                className="inline-flex items-center px-1.5 py-1 border border-transparent text-xs font-medium rounded text-white bg-gray-600 hover:bg-gray-700"
                                title="取消"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <span>{item.palletNumber}</span>
                              <button
                                onClick={() => startEditPallet(item)}
                                className="inline-flex items-center px-1.5 py-0.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                                title="編輯棧板號碼"
                              >
                                ✏️
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {getRfidLast5(item.rfid) ? (
                            <button
                              onClick={() => handleOutbound(item)}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                            >
                              出庫
                            </button>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 text-sm text-gray-400">
                              已出庫
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(item.last_updated)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {filteredItems.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    {searchValue ? '未找到符合條件的項目' : '暫無庫存項目'}
                  </div>
                )}
              </div>

              {/* 分頁控制 */}
              {filteredItems.length > itemsPerPage && (
                <div className="flex justify-between items-center mt-4">
                  <div className="text-sm text-gray-700">
                    顯示 {startIndex + 1} 到 {Math.min(endIndex, filteredItems.length)} 筆，共 {filteredItems.length} 筆
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      上一頁
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-1 text-sm border rounded-md ${
                          currentPage === page
                            ? 'bg-blue-500 text-white border-blue-500'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      下一頁
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'pending' && (
            <div>
              {/* 待清點項目標題 */}
              <div className="bg-yellow-50 rounded-lg p-4 mb-6 border border-yellow-200">
                <h3 className="text-lg font-medium text-gray-900">待清點項目</h3>
                <p className="text-sm text-gray-600 mt-1">顯示所有有 RFID 但尚未分配棧板號碼的項目</p>
              </div>

              {/* 待清點表格 */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                        批號
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                        RFID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                        狀態
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        入庫時間
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                        操作
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredItems.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                          {getRfidLast5(item.rfid)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            待清點
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(item.last_updated)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {editingItemId === item.id ? (
                            <div className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={newPalletNumber}
                                onChange={(e) => setNewPalletNumber(e.target.value)}
                                placeholder="輸入棧板號碼"
                                className="w-24 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                autoFocus
                              />
                              <button
                                onClick={() => handleUpdatePalletNumber(item)}
                                className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                              >
                                ✓
                              </button>
                              <button
                                onClick={cancelEditPallet}
                                className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => startEditPallet(item)}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                            >
                              分配棧板
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {filteredItems.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    暫無待清點項目
                  </div>
                )}
              </div>

              {/* 統計資訊 */}
              <div className="mt-4 flex justify-between text-sm text-gray-600">
                <span>共 {filteredItems.length} 個待清點項目</span>
                <span className="text-yellow-600 font-medium">這些項目需要分配棧板號碼</span>
              </div>
            </div>
          )}

          {activeTab === 'inbound' && (
            <div>
              {/* 操作模式選擇 - 四個按鈕 */}
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={() => setOperationMode('single-inbound')}
                    className={`px-6 py-3 rounded-lg font-medium text-base transition-colors duration-200 ${
                      operationMode === 'single-inbound'
                        ? 'bg-blue-500 text-white shadow-lg'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-blue-300 hover:text-white'
                    }`}
                  >
                    📦 單次入庫
                  </button>
                  <button
                    onClick={() => setOperationMode('batch-inbound')}
                    className={`px-6 py-3 rounded-lg font-medium text-base transition-colors duration-200 ${
                      operationMode === 'batch-inbound'
                        ? 'bg-blue-500 text-white shadow-lg'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-blue-300 hover:text-white'
                    }`}
                  >
                    📋 批次入庫
                  </button>
                  <button
                    onClick={() => setOperationMode('batch-outbound')}
                    className={`px-6 py-3 rounded-lg font-medium text-base transition-colors duration-200 ${
                      operationMode === 'batch-outbound'
                        ? 'bg-red-500 text-white shadow-lg'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-red-300 hover:text-white'
                    }`}
                  >
                    📤 批次出庫
                  </button>
                  <button
                    onClick={() => setOperationMode('batch-query')}
                    className={`px-6 py-3 rounded-lg font-medium text-base transition-colors duration-200 ${
                      operationMode === 'batch-query'
                        ? 'bg-green-500 text-white shadow-lg'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-green-300 hover:text-white'
                    }`}
                  >
                    🔍 批號查詢
                  </button>
                </div>
              </div>

              {/* 單次入庫表單 */}
              {operationMode === 'single-inbound' && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">📦 單次入庫</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        批號 *
                      </label>
                      <input
                        type="text"
                        value={inboundBatchNumber}
                        onChange={(e) => setInboundBatchNumber(e.target.value)}
                        placeholder="請輸入批號..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        RFID後五碼 *
                      </label>
                      <input
                        type="text"
                        value={inboundRFID}
                        onChange={(e) => setInboundRFID(e.target.value)}
                        placeholder="請輸入RFID後五碼 (5位數字)..."
                        maxLength={5}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div className="mt-6">
                    <FormButton 
                      caption="確定入庫" 
                      onClick={handleInbound}
                      disabled={!inboundBatchNumber.trim() || !inboundRFID.trim()}
                    />
                  </div>
                </div>
              )}

              {/* 批次入庫表單 */}
              {operationMode === 'batch-inbound' && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                  <div className="text-6xl mb-4">🚧</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">📋 批次入庫</h3>
                  <p className="text-gray-500">此功能正在開發中...</p>
                </div>
              )}

              {/* 批次出庫表單 */}
              {operationMode === 'batch-outbound' && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                  <div className="text-6xl mb-4">🚧</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">📤 批次出庫</h3>
                  <p className="text-gray-500">此功能正在開發中...</p>
                </div>
              )}
              
              {/* 批號查詢表單 */}
              {operationMode === 'batch-query' && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">🔍 批號查詢</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        輸入批號
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={queryBatchNumber}
                          onChange={(e) => setQueryBatchNumber(e.target.value)}
                          placeholder="請輸入要查詢的批號..."
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                        <FormButton 
                          caption="查詢" 
                          onClick={handleBatchQuery}
                          disabled={!queryBatchNumber.trim() || batchQueryLoading}
                        />
                      </div>
                    </div>
                    
                    {/* 查詢結果顯示 */}
                    {batchQueryLoading && (
                      <div className="text-center py-4">
                        <div className="text-gray-500">查詢中...</div>
                      </div>
                    )}
                    
                    {batchQueryResult && !batchQueryLoading && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-medium text-gray-900 mb-2">查詢結果：</h4>
                        {batchQueryResult.data && batchQueryResult.data.length > 0 ? (
                          <div className="space-y-2">
                            <p className="text-sm text-gray-600">
                              批號 <span className="font-medium">{queryBatchNumber}</span> 的相關資訊：
                            </p>
                            <div className="bg-white p-3 rounded border border-gray-200">
                              {batchQueryResult.data.map((item: any, index: number) => (
                                <div key={index} className="mb-2 last:mb-0">
                                  <p className="text-sm">
                                    <span className="text-gray-600">RFID後五碼：</span>
                                    <span className="font-mono font-medium">{getRfidLast5(item.rfid)}</span>
                                  </p>
                                  <p className="text-sm">
                                    <span className="text-gray-600">棧板號碼：</span>
                                    <span className="font-medium">{item.palletNumber || '未分配'}</span>
                                  </p>
                                  {item.palletNumber === 'OUT' && (
                                    <p className="text-sm text-red-600">狀態：已出庫</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">找不到此批號的相關資料</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'outbound' && (
            <div>
              {/* 已出庫項目標題 */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-medium text-gray-900">已出庫項目</h3>
                <p className="text-sm text-gray-600 mt-1">顯示所有已經出庫的項目（棧板號碼為 OUT）</p>
              </div>

              {/* 已出庫表格 */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                        批號
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                        RFID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                        棧板號碼
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        出庫時間
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredItems.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                          {getRfidLast5(item.rfid)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {item.palletNumber || '空白'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(item.last_updated)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {filteredItems.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    暫無已出庫項目
                  </div>
                )}
              </div>

              {/* 統計資訊 */}
              <div className="mt-4 text-sm text-gray-600">
                共 {filteredItems.length} 個已出庫項目
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Inventory;